import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  CheckCircle,
  LoaderCircle,
  MinusCircle,
  TriangleAlert,
  XCircle,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { WorkspaceDocumentType } from "@/models/workspace/Workspace";
import {
  getWorkspaceFileIndexingStatusPresentation,
  WorkspaceFileIndexingStatusVariant,
} from "@/pages/workspace/utils/workspaceItemUtils";

interface IndexingStatusIconProps {
  isIndexed: boolean;
  searchIndexStale?: boolean;
  documentType?: WorkspaceDocumentType;
  /** A kildeklargøring run is in flight for the document. */
  isIndexing?: boolean;
  /**
   * Replaces the tooltip while a run is in flight, with where the run is
   * (LAR-486). Surfaces without progress data leave it out.
   */
  indexingTooltip?: ReactNode;
  /** Search hits only flag what needs attention: a healthy or non-indexable document shows nothing. */
  hideHealthy?: boolean;
  className?: string;
}

const ICONS: Record<
  WorkspaceFileIndexingStatusVariant,
  { Icon: LucideIcon; tone: string }
> = {
  indexing: { Icon: LoaderCircle, tone: "animate-spin text-muted-foreground" },
  indexed: { Icon: CheckCircle, tone: "text-success" },
  stale: { Icon: TriangleAlert, tone: "text-warning" },
  actionNeeded: { Icon: XCircle, tone: "text-destructive" },
  notApplicable: { Icon: MinusCircle, tone: "text-muted-foreground" },
};

/**
 * The one indexing-status marker for workspace documents, shared by the files tab, the
 * Workspaces page content search and global search (LAR-235). The colour carries the
 * meaning: green is ready, amber is an index from before a platform upgrade (still
 * usable, rerun kildeklargøring), red is not ready at all. The tooltip says the same in
 * words, and the icon exposes it as its accessible name.
 */
export const IndexingStatusIcon = ({
  isIndexed,
  searchIndexStale = false,
  documentType,
  isIndexing = false,
  indexingTooltip,
  hideHealthy = false,
  className,
}: IndexingStatusIconProps) => {
  const { variant, tooltip } = getWorkspaceFileIndexingStatusPresentation({
    isIndexed,
    searchIndexStale,
    documentType,
    isIndexing,
  });

  if (hideHealthy && (variant === "indexed" || variant === "notApplicable")) {
    return null;
  }

  const { Icon, tone } = ICONS[variant];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A still box around the icon: the tooltip is placed on its trigger's
            box, and a spinning icon's box grows and shrinks as it turns, which
            made the tooltip bob. The box is also the whole hover target, where
            the icon alone only reacts on its thin stroke. */}
        <span
          className={cn("inline-flex size-4 shrink-0", className)}
          aria-label={tooltip}
          role="img"
          tabIndex={0}
        >
          <Icon className={cn("size-full", tone)} aria-hidden="true" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[300px]">
        {variant === "indexing" && indexingTooltip ? (
          <div className="text-xs leading-snug">{indexingTooltip}</div>
        ) : (
          <p className="text-xs leading-snug">{tooltip}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

export default IndexingStatusIcon;
